import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { Bell, BellOff, Smartphone, MonitorSmartphone } from "lucide-react";
import {
  pushSupported,
  currentPushStatus,
  subscribePush,
  unsubscribePush,
  testPush,
} from "@/lib/pushClient";

export default function PushSettingsCard() {
  const [state, setState] = useState({ supported: false, permission: "default", subscribed: false });
  const [busy, setBusy] = useState(false);
  const supported = pushSupported();

  const refresh = async () => {
    const s = await currentPushStatus();
    setState(s);
  };

  useEffect(() => {
    refresh();
  }, []);

  const onEnable = async () => {
    setBusy(true);
    try {
      await subscribePush();
      toast.success("Push notifications enabled on this device");
      await refresh();
    } catch (e) {
      toast.error(e.message || "Could not enable push notifications");
    } finally {
      setBusy(false);
    }
  };

  const onDisable = async () => {
    setBusy(true);
    try {
      await unsubscribePush();
      toast.success("Push notifications disabled on this device");
      await refresh();
    } catch (e) {
      toast.error(e.message || "Could not disable");
    } finally {
      setBusy(false);
    }
  };

  const onTest = async () => {
    setBusy(true);
    try {
      const r = await testPush();
      toast.success(`Test sent (${r.sent} device${r.sent === 1 ? "" : "s"})`);
    } catch (e) {
      toast.error(e.message || "Test failed");
    } finally {
      setBusy(false);
    }
  };

  const isIOS = typeof navigator !== "undefined" && /iPhone|iPad|iPod/.test(navigator.userAgent);
  const isStandalone =
    typeof window !== "undefined" &&
    (window.matchMedia?.("(display-mode: standalone)").matches || window.navigator?.standalone);

  return (
    <Card data-testid="push-settings-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Bell className="h-4 w-4" /> Push notifications
        </CardTitle>
        <CardDescription>
          Get pinged on your phone or laptop for CRITICAL pain events, ghost-pitcher alerts,
          and workload verifications — even when ArmIQ is closed.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {!supported && (
          <div className="rounded border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-amber-200">
            This browser doesn't support web push. Try Chrome, Edge, Firefox, or install ArmIQ
            to your home screen on iOS 16.4+.
          </div>
        )}

        {supported && isIOS && !isStandalone && (
          <div className="rounded border border-cyan-500/40 bg-cyan-500/10 p-3 text-sm text-cyan-100">
            <strong>iPhone / iPad:</strong> Tap the Share icon → <em>Add to Home Screen</em>, then
            open ArmIQ from your home screen to enable push notifications.
          </div>
        )}

        {supported && (
          <div className="flex items-center gap-3 rounded border border-white/10 bg-white/5 p-3 text-sm">
            <div className="flex items-center gap-2">
              {state.subscribed ? <Bell className="h-4 w-4 text-emerald-400" /> : <BellOff className="h-4 w-4 text-muted-foreground" />}
              <span>
                {state.permission === "denied"
                  ? "Blocked — change in browser site settings"
                  : state.subscribed
                  ? "Enabled on this device"
                  : "Not subscribed"}
              </span>
            </div>
          </div>
        )}

        <div className="flex flex-wrap gap-2">
          {supported && !state.subscribed && state.permission !== "denied" && (
            <Button onClick={onEnable} disabled={busy} data-testid="push-enable-btn">
              <Bell className="mr-2 h-4 w-4" /> Enable on this device
            </Button>
          )}
          {supported && state.subscribed && (
            <>
              <Button variant="outline" onClick={onTest} disabled={busy} data-testid="push-test-btn">
                Send test
              </Button>
              <Button variant="outline" onClick={onDisable} disabled={busy} data-testid="push-disable-btn">
                <BellOff className="mr-2 h-4 w-4" /> Turn off
              </Button>
            </>
          )}
        </div>

        <div className="flex items-start gap-3 rounded border border-white/5 bg-white/[0.03] p-3 text-xs text-muted-foreground">
          <MonitorSmartphone className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            One subscription per device + browser. Repeat this on every laptop / phone where you
            want push. Works on Android Chrome, all desktops, and iOS 16.4+ as an installed PWA.
          </span>
        </div>
      </CardContent>
    </Card>
  );
}
